/**
 * /overview — the Executive Overview drawn natively from Supabase (spec
 * 2026-10-14). Composition only: reads in useOverviewData, numbers in
 * buildOverviewView, boxes in OverviewGrid. Period, objective and currency
 * are the dashboards' shared filters (URL + memory, D2 plan R19); the channel
 * selection stays D1's own.
 *
 * @module pages/ExecutiveOverview
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useIsPlatformMaster } from '@/hooks/useIsPlatformMaster';
import { useChannelFilter } from '@/contexts/ChannelFilterContext';
import { AD_CHANNEL_IDS } from '@/services/channelFilter';
import { soleChannelConnectedOf } from '@/services/channels/channelView';
import { YDMPageHero } from '@/components/brand';
import { DataStatusPill } from '@/components/looker/DataStatusPill';
import { BackfillStatusPill } from '@/components/looker/BackfillStatusPill';
import { PRINT_FRAME_WIDTH_PX, usePrintFrame } from '@/hooks/usePrintFrame';
import { useOverviewData, type OverviewScope } from '@/hooks/useOverviewData';
import { useOverviewAppBoxes } from '@/hooks/useOverviewAppBoxes';
import { useDashboardFilters } from '@/hooks/useDashboardFilters';
import { useHeldChatContext } from '@/hooks/useHeldChatContext';
import { linkSearchOf } from '@/services/dashboardFilters';
import { OverviewFilterBar } from '@/components/overview/OverviewFilterBar';
import { OverviewGrid } from '@/components/overview/OverviewGrid';
import { PrintHeader } from '@/components/overview/PrintHeader';
import { buildOverviewView } from '@/services/overview/overviewView';
import { coverageInCurrencyOf, latestDataDateOf, pacingCurrencyOf } from '@/services/overview/overviewHeader';
import MediaChatBubble from '@/components/MediaChatBubble';
import { ExportMenu } from '@/components/looker/ExportMenu';
import { OVERVIEW_ADVISOR } from '@/config/agentProfiles';
import { csvLabelsFrom } from '@/services/looker/lookerCsv';
import { coverageLabelsFrom } from '@/services/looker/lookerCoverageLabels';
import { platformLabel } from '@/utils/platformLabel';
import { objectiveText } from '@/services/overview/overviewFormat';
import { appTitlesFrom, overviewTablesOf, type OverviewPageInput } from '@/services/overview/overviewTables';
import { buildOverviewChatContext } from '@/services/overview/overviewChatContext';
import { downloadOverviewCsv, pageSettled } from '@/services/overview/overviewCsv';

const ExecutiveOverview: React.FC = () => {
  const { t } = useTranslation();
  const { workspace, workspaces } = useWorkspace();
  const { isMaster } = useIsPlatformMaster(true);
  const [viewAll, setViewAll] = useState(false);
  // R19: the URL, else this browser's memory, else 28 days / every objective / the most spent.
  const { shared, setShared } = useDashboardFilters();
  const { period: token, objective, currency } = shared;

  const workspaceId = workspace?.id ?? null;
  const showAll = isMaster && viewAll;
  // R19: no currency reset here. currencyChoiceOf checks a remembered or URL
  // currency against the new scope and falls back to the most spent.
  useEffect(() => {
    setViewAll(false);
  }, [workspaceId]);

  // R10: the master's all-workspaces scope is every workspace the app lists for them.
  const idsKey = workspaces.map((w) => w.id).join(',');
  const scope = useMemo<OverviewScope | null>(() => {
    if (showAll) return { workspaceIds: idsKey ? idsKey.split(',') : [], workspaceId: null };
    return workspaceId ? { workspaceIds: [workspaceId], workspaceId } : null;
  }, [showAll, idsKey, workspaceId]);

  // The remembered channel selection, as display-ordered ids; [] = every channel.
  const { selected, filtering } = useChannelFilter();
  const channelKey = filtering ? AD_CHANNEL_IDS.filter((id) => selected.has(id)).join(',') : '';
  const channels = useMemo(() => (channelKey ? channelKey.split(',') : []), [channelKey]);

  const data = useOverviewData(scope, token);
  const connected = useMemo(
    () => (data.freshness.status === 'ready' ? data.freshness.channels.map((c) => c.platform) : []),
    [data.freshness],
  );
  // rules-04 G3: whether the one ticked channel is connected, from the same statuses read; unknown in all workspaces.
  const channelConnected = soleChannelConnectedOf(channels, data.freshness, showAll);
  const view = useMemo(
    () => (data.windows
      ? buildOverviewView({
        windows: data.windows, facts: data.facts, summary: data.summary, mapping: data.mapping, targets: data.targets,
        coverage: data.coverage, connected, channelConnected, channels, objective, currency,
      })
      : null),
    [data, connected, channelConnected, channels, objective, currency],
  );
  const app = useOverviewAppBoxes(scope?.workspaceId ?? null, data.windows?.current ?? null, pacingCurrencyOf(data.facts, view));
  // Box 8 and its CSV / chat lines (rules-04 B2): with spend in several currencies they say coverage covers every
  // currency; the naming share, a count of campaigns, stands (rules-04 E2). The view keeps data.coverage: its Result
  // chips do not use the currency, and the view decides the currency.
  const coverage = useMemo(
    () => coverageInCurrencyOf(data.coverage, view?.money ?? null, data.facts.status),
    [data.coverage, view?.money, data.facts.status],
  );
  // I1: box 11 / 13 open D2 in the currency D1 SHOWS (its most spent when none is picked), so D2's box 2 is
  // the row that was clicked. D2 then remembers it as picked: the value D1 was showing anyway.
  // I2: and in the master's all-workspaces view (scope=all) exactly while D1 shows it.
  const shownCurrency = view?.money?.currency ?? currency;
  const linkSearch = useMemo(
    () => linkSearchOf({ period: token, objective, currency: shownCurrency }, showAll),
    [token, objective, shownCurrency, showAll],
  );
  const printFrame = usePrintFrame();

  const w = data.windows;
  const periodLine = w ? t('looker.periodLine', { start: w.current.start, end: w.current.end, prevStart: w.previous.start, prevEnd: w.previous.end }) : null;
  const latest = latestDataDateOf(data.freshness, view);
  const factsLoading = data.facts.status === 'loading';
  const csvLabels = useMemo(() => csvLabelsFrom(t), [t]);
  const pageInput = useMemo<OverviewPageInput | null>(() => {
    if (!w || !view) return null;
    const objectiveLabel = objective === 'all' ? t('looker.objectives.all') : objectiveText(objective, t);
    return {
      title: t('overview.title'), allWorkspaces: showAll, workspaceName: showAll ? null : workspace?.name ?? null,
      windows: w, latestDataDate: latest, money: view.money, mapping: data.mapping, channels, objectiveLabel,
      filters: [
        { title: t('overview.filters.objective'), value: objectiveLabel },
        ...(channels.length > 0 ? [{ title: t('overview.filters.channels'), value: channels.map((c) => platformLabel(c)).join(', ') }] : []),
      ],
      tables: overviewTablesOf({ view, windows: w, mapping: data.mapping, t, labels: csvLabels }),
      app, appTitles: appTitlesFrom(t),
      coverage: { state: coverage, labels: coverageLabelsFrom(t), channels },
      backfill: data.backfill, freshness: data.freshness,
    };
  }, [w, view, objective, t, showAll, workspace?.name, latest, data.mapping, channels, csvLabels, app, coverage, data.backfill, data.freshness]);
  // Under the not-connected card the facts boxes are EMPTY at once (rules-04 H3): while the facts load, box 6 holds the
  // file (pacingCurrencyOf → undefined). `data.coverage` is read raw here, not the page-currency `coverage` above.
  const settled = pageInput !== null && view !== null && pageSettled(view, app, data);
  // The chat is told once the facts boxes settled (a loading app box is simply not told yet) and, as on D2, the
  // statuses read: the card's words wait on it (rules-04 G3). Idle (all workspaces) or failed holds nothing.
  const chatContext = useMemo(
    () => (pageInput && view && view.scorecards.status !== 'loading' && data.freshness.status !== 'loading'
      ? buildOverviewChatContext(pageInput)
      : null),
    [pageInput, view, data.freshness.status],
  );
  const chatScope = `overview:${showAll ? 'all' : workspaceId ?? ''}`;
  // A filter or the period keeps the conversation: while the page has no new data yet (its reads are loading, or it
  // refuses the period) the chat holds the last data and takes no new question. A new scope (another workspace, all
  // workspaces) starts a new conversation.
  const chat = useHeldChatContext(chatContext, chatScope);

  return (
    <div className="space-y-4 p-4 md:p-6 print:p-0">
      <div className="print:hidden">
        <YDMPageHero eyebrow={showAll ? t('looker.allWorkspaces') : workspace?.name} headline={t('overview.title')} subtitle={t('overview.subtitle')} className="mb-2" />
      </div>
      <PrintHeader title={t('overview.title')} scope={showAll ? t('looker.allWorkspaces') : workspace?.name ?? null} dataDate={latest} />

      {/* Row F and row 1: the Dashboard's control band. */}
      <section data-overview-controls className="print:hidden space-y-4 rounded-2xl border border-neutral-200 border-l-[5px] border-l-primary-500 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <OverviewFilterBar
            token={token} onToken={(period) => setShared({ period })}
            objective={objective} onObjective={(o) => setShared({ objective: o })}
            money={view?.money ?? null} onCurrency={(c) => setShared({ currency: c })} disabled={factsLoading}
          />
          <div className="flex flex-wrap items-center gap-3">
            <DataStatusPill latestDataDate={latest} state={data.freshness} />
            {w?.cutApplied && <span className="text-xs font-medium text-neutral-800">{t('looker.dataStatus.unsettled')}</span>}
            <BackfillStatusPill state={data.backfill} />
            <ExportMenu
              pdfDisabled={!view || factsLoading || printFrame.framing}
              onPdf={printFrame.start}
              csvDisabled={!settled}
              onCsv={() => pageInput && downloadOverviewCsv(pageInput, csvLabels, t)}
            />
            {isMaster && (
              <label className="inline-flex items-center gap-2 text-sm font-medium text-ink-900">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-400 accent-ink-950"
                  checked={viewAll}
                  onChange={(e) => setViewAll(e.target.checked)}
                />
                {t('looker.viewAllWorkspaces')}
              </label>
            )}
          </div>
        </div>
        {periodLine && <p className="text-xs text-neutral-700">{periodLine}</p>}
        {showAll && <p role="status" className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-ink-950">{t('looker.allWorkspacesBanner')}</p>}
      </section>

      {periodLine && <p className="hidden text-xs text-neutral-800 print:block">{periodLine}</p>}

      {!scope && <p className="text-sm text-neutral-700">{t('looker.loading')}</p>}
      {scope && !w && <p className="text-sm text-neutral-800">{t('looker.badDateRange')}</p>}

      {scope && w && view && (
        <div
          className="[-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
          style={printFrame.framing ? { width: PRINT_FRAME_WIDTH_PX, maxWidth: '100%' } : undefined}
        >
          {/* Landscape A4 for this page only; the rule leaves with the page. */}
          <style>{'@page { size: A4 landscape; margin: 10mm; }'}</style>
          <OverviewGrid
            view={view} windows={w} mapping={data.mapping} app={app} coverage={coverage}
            channelFiltered={channels.length > 0} linkSearch={linkSearch}
          />
        </div>
      )}

      {chat.context && (
        <div className="print:hidden">
          <MediaChatBubble
            key={chatScope}
            mode="overview"
            lookerContext={chat.context}
            contextPending={chat.pending}
            scope={chatScope}
            title={t('overview.chatTitle')}
            subtitle={t('looker.chatSubtitle')}
            guidedQuestions={OVERVIEW_ADVISOR.guidedQuestions}
          />
        </div>
      )}
    </div>
  );
};

export default ExecutiveOverview;
